import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check, ArrowRight, Layers, Brain, ShieldCheck, Boxes, Landmark, GraduationCap } from 'lucide-react';
import { serviceDetails, servicePageCopy, SLUG_TO_ID } from '@/i18n.services';
import SEO from '@/components/SEO';

const ICONS = {
  platforms: Layers,
  ai: Brain,
  security: ShieldCheck,
  blockchain: Boxes,
  banking: Landmark,
  cauracademy: GraduationCap,
};

const ServicePage = ({ lang }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const id = SLUG_TO_ID[slug];
  const data = id ? serviceDetails[lang][id] : null;
  const copy = servicePageCopy[lang];

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [slug]);

  if (!data) {
    return (
      <main className="min-h-[60vh] grid place-items-center px-6">
        <div className="text-center">
          <div className="kicker-mono text-white/60 mb-2">404</div>
          <h1 className="font-display text-3xl font-[650] text-white">{lang === 'es' ? 'Servicio no encontrado' : 'Service not found'}</h1>
          <Link to="/" className="btn-primary mt-6 inline-flex" data-testid="service-404-home">
            {copy.back_to_home}
          </Link>
        </div>
      </main>
    );
  }

  const Icon = ICONS[data.id] || Layers;

  return (
    <main data-testid="service-page">
      <SEO
        lang={lang}
        path={`/servicios/${slug}`}
        title={data.title}
        description={data.subtitle}
      />
      {/* Hero */}
      <section className="bg-grid relative pt-16 pb-16 sm:pt-20 sm:pb-20">
        <div className="orb float-slow" style={{ width: 360, height: 360, background: 'rgba(5,126,243,.35)', top: '-80px', left: '-80px' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/#services" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors" data-testid="service-back-link">
            {copy.back_to_home}
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="kicker-mono mb-4" data-testid="service-kicker">{data.kicker}</div>
              <h1 className="font-display neon-title text-4xl sm:text-5xl lg:text-6xl font-[700] tracking-[-0.02em] leading-[1.05]" data-testid="service-title">
                {data.title}
              </h1>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed" data-testid="service-subtitle">
                {data.subtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/agenda" state={{ service: data.id }} className="btn-primary" data-testid="service-hero-cta">
                  {copy.cta_button} <ArrowRight size={16} />
                </Link>
                <button onClick={() => navigate('/#services')} className="btn-ghost" data-testid="service-hero-secondary">
                  {copy.back_to_services}
                </button>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="gradient-border">
                <div className="inner p-10 flex items-center justify-center">
                  <div className="h-24 w-24 rounded-2xl bg-[rgba(5,126,243,.12)] border border-[rgba(5,126,243,.4)] flex items-center justify-center text-[rgb(var(--brand-electric))] shadow-[var(--glow-electric)]">
                    <Icon size={52} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 bg-[rgb(var(--bg-1))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="kicker-mono mb-3">{copy.capabilities}</div>
          <div className="grid sm:grid-cols-2 gap-5">
            {data.capabilities.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="rounded-2xl border border-white/10 bg-[rgb(var(--bg-2))] p-6 hover:border-[rgba(5,126,243,.5)] transition-colors"
                data-testid={`capability-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-9 w-9 shrink-0 rounded-lg bg-[rgba(5,126,243,.12)] border border-[rgba(5,126,243,.35)] flex items-center justify-center text-[rgb(var(--brand-electric))]">
                    <Check size={18} />
                  </div>
                  <div>
                    <div className="font-display text-lg font-[650] text-white">{c.title}</div>
                    <div className="mt-1.5 text-sm text-white/65 leading-relaxed">{c.desc}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases + Methodology (2 col) */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="kicker-mono mb-3">{copy.use_cases}</div>
            <div className="space-y-3">
              {data.use_cases.map((u, i) => (
                <div key={u.title} className="rounded-xl border border-white/10 bg-[rgb(var(--bg-1))] p-5" data-testid={`usecase-${i}`}>
                  <div className="font-display text-base font-[650] text-white">{u.title}</div>
                  <div className="mt-1 text-sm text-white/65 leading-relaxed">{u.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="kicker-mono mb-3">{copy.methodology}</div>
            <div className="space-y-3">
              {data.methodology.map((m, i) => (
                <div key={m.title} className="rounded-xl border border-white/10 bg-[rgb(var(--bg-1))] p-5 relative" data-testid={`method-${i}`}>
                  <span className="absolute -left-3 top-5 w-6 h-6 rounded-full bg-[rgb(var(--brand-electric))] text-white text-xs font-semibold grid place-items-center shadow-[var(--glow-electric)]">{i + 1}</span>
                  <div className="pl-6 font-display text-base font-[650] text-white">{m.title}</div>
                  <div className="pl-6 mt-1 text-sm text-white/65 leading-relaxed">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="py-16 bg-[rgb(var(--bg-1))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="kicker-mono mb-5">{copy.tech}</div>
          <div className="flex flex-wrap justify-center gap-2.5" data-testid="service-tech-stack">
            {data.tech.map((x) => (
              <span key={x} className="chip"><span className="dot" />{x}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[rgb(var(--bg-0))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-[650] text-white tracking-tight">{copy.cta_title}</h2>
          <p className="mt-3 text-white/70">{copy.cta_subtitle}</p>
          <Link to="/agenda" state={{ service: data.id }} className="btn-primary mt-7 inline-flex" data-testid="service-bottom-cta">
            {copy.cta_button} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServicePage;
